import dynamic from 'next/dynamic';
import Script from 'next/script';

// Interfaces
import { type BrandedArticle, PageType } from 'interfaces/content/articles/Post';

// Constants
import getArticlePageTestId from 'constants/testsIds/articlePage';

// Utils
import RawHtml from 'utils/miscUtils/rawHtml';
import { scrubContent } from 'utils/stringUtils';
import { trackVevEvent } from 'services/Gtm/functions';

// Components
import InStream from 'components/Ad/AdSlot/InStream';
import { useEffect, type JSX } from 'react';
import { parseEmbeddedCode } from 'utils/parseEmbed';
import BrandedEmbed from 'components/Embed/BrandedEmbed';
import * as S from './BrandedArticleStyles';
import getCommonTestId from '@/main/constants/testsIds/common';

const Hero = dynamic(() => import('components/BrandedArticles/Hero'));
const ContentWithEmbed = dynamic(() => import('components/ContentWithEmbed'));

interface BrandedArticleProps {
  brandedArticle: BrandedArticle;
  lastPWithoutAds?: number;
  embeds: { node: string; index: number; component: JSX.Element }[];
  adFrequency: number;
  hideInstreamAds?: boolean;
}

const BrandedArticlePage = ({
  brandedArticle,
  embeds,
  adFrequency,
  lastPWithoutAds,
  hideInstreamAds = false,
}: BrandedArticleProps) => {
  const cleanedContent = scrubContent(brandedArticle.content);
  const {
    postSettings,
    vodVideoId,
    imageCaption,
    imageCredit,
    templateType,
    featuredMediaType,
    authors,
    partnerLogo,
    fortuneLogo,
    partnerLogoLink,
    paidContentLabel,
    scriptSource,
    embeddedCode,
    hideAuthors,
  } = brandedArticle;

  const vevEventListener = (event) => {
    trackVevEvent(event);
  };

  useEffect(() => {
    window.addEventListener('vev.track', vevEventListener);
    return () => {
      window.removeEventListener('vev.track', vevEventListener);
    };
  }, []);

  if (templateType === 'custom-iframe') {
    const parsedEmbed = parseEmbeddedCode(embeddedCode || '');

    if (parsedEmbed) {
      return <BrandedEmbed data={parsedEmbed} />;
    }

    if (scriptSource) {
      return (
        <Script
          src={scriptSource}
          strategy='beforeInteractive'
        />
      );
    }
  }

  return (
    <S.Container>
      <Hero
        title={brandedArticle.title}
        description={brandedArticle.excerpt}
        postSettings={postSettings}
        videoId={vodVideoId}
        imageCaption={imageCaption}
        imageCredit={imageCredit}
        templateType={templateType}
        featuredMediaType={featuredMediaType}
        authors={authors}
        hideAuthors={hideAuthors}
        partnerLogo={partnerLogo}
        fortuneLogo={fortuneLogo}
        partnerLogoLink={partnerLogoLink}
        paidContentLabel={paidContentLabel}
      />
      <S.ContentWrapper
        $templateType={templateType}
        data-cy={getCommonTestId('BRANDED_ARTICLE_CONTENT')}
      >
        <ContentWithEmbed
          embedInstructions={embeds.map((embed) => ({ index: embed.index, node: embed.node }))}
          adFrequency={adFrequency}
          lastPWithoutAds={lastPWithoutAds}
          pageType={PageType.ARTICLE_V2}
          section={brandedArticle.primarySection.name}
          articleUrl={brandedArticle.url}
          isBranded
        >
          {[
            ...embeds.map((embed) => embed.component),
            <div
              key={`article-content-${adFrequency}`}
              data-cy={getArticlePageTestId('BRANDED_ARTICLE')}
            >
              {RawHtml({ html: cleanedContent })}
            </div>,
          ]}
        </ContentWithEmbed>

        <InStream
          index={1}
          renderAds={!hideInstreamAds}
          addPadding
        />
      </S.ContentWrapper>
    </S.Container>
  );
};

export default BrandedArticlePage;
